import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom';
import {
  Layout,
  Box,
  Skeleton,
  ThemeProvider,
  Button,
  Text,
} from '@dingus/dingus-ui';
import AuthCallback from './components/AuthCallback';
import { NotificationProvider } from './context/NotificationContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { api } from './services/api';
import FeedbackButton from './components/FeedbackButton';
import { DashboardIcon, CommitIcon, CalendarIcon, GearIcon, UsersIcon, TeamsIcon, BarChartIcon, CubeIcon, IdCardIcon } from "@dingus/dingus-ui/icons";
import { ROLE_ADMIN, ROLE_MANAGER } from './context/roles';

// Lazy load de pantallas
const DailyDashboardScreen = lazy(() => import('./screens/DailyDashboardScreen'));
const UserManagementScreen = lazy(() => import('./screens/UserManagementScreen'));
const TeamsScreen = lazy(() => import('./screens/TeamsScreen'));
const JiraProjectsScreen = lazy(() => import('./screens/JiraProjectsScreen'));
const RolesScreen = lazy(() => import('./screens/RolesScreen'));
const VacationCalendarScreen = lazy(() => import('./screens/VacationCalendarScreen'));
const HolidayCalendarManagementScreen = lazy(() => import('./screens/HolidayCalendarManagementScreen'));
const CommitsViewScreen = lazy(() => import('./screens/CommitsViewScreen'));
const JiraDashboardScreen = lazy(() => import('./screens/JiraDashboardScreen'));
const ConfigurationPanelScreen = lazy(() => import('./screens/ConfigurationPanelScreen'));

const themeConfig = {
  accentColor: 'teal',
  forcedTheme: 'light'
};

const LoadingScreen = () => (
  <Box
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100vw',
      position: 'fixed',
      top: 0,
      left: 0,
      backgroundColor: '#ffffff',
      zIndex: 9999,
    }}
  >
    <Skeleton height="400px" width="80%" />
    <Box style={{ marginTop: '16px', fontSize: '16px', color: '#666' }}>
      Cargando...
    </Box>
  </Box>
);

const AccessDeniedPage = () => {
  const { logout } = useAuth();
  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        gap: '24px',
        backgroundColor: '#f5f5f5',
        padding: 24,
      }}
    >
      <Text as="h1" style={{ fontSize: '24px', color: '#333', textAlign: 'center' }}>
        No tiene permisos para acceder a la aplicación.
      </Text>
      <Text style={{ color: '#666', textAlign: 'center', maxWidth: 480 }}>
        Su usuario no tiene equipos asignados. Contacte al administrador si cree que es un error.
      </Text>
      <Button onClick={logout} variant="primary">
        Cerrar sesión
      </Button>
    </Box>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading, accessDenied } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (accessDenied) {
    return <AccessDeniedPage />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading, role } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (role !== ROLE_ADMIN) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const ManagerOrAdminRoute = ({ children }) => {
  const { user, loading, role } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (role !== ROLE_ADMIN && role !== ROLE_MANAGER) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const LoginPage = () => {
  const { login } = useAuth();

  useEffect(() => {
    login();
  }, [login]);

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        gap: '16px',
        backgroundColor: '#ffffff',
      }}
    >
      <Skeleton height="400px" width="80%" />
      <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>
        Redirigiendo al proveedor de autenticación...
      </h2>
    </Box>
  );
};

const AppLayout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, role } = useAuth();
  const [userAvatar, setUserAvatar] = useState('');

  useEffect(() => {
    const fetchUserAvatar = async () => {
      if (user?.slack_id) {
        try {
          const slackUsers = await api.getSlackUsers();
          const slackUser = slackUsers.find(u => u.id === user.slack_id);
          if (slackUser) {
            const avatarUrl = slackUser.profile_image ||
              slackUser.profile?.image_192 ||
              slackUser.profile?.image_72 ||
              slackUser.profile?.image_original;
            setUserAvatar(avatarUrl && avatarUrl.startsWith('http') ? avatarUrl : '');
          } else {
            setUserAvatar('');
          }
        } catch (error) {
          console.error('Error al obtener el avatar del usuario:', error);
          setUserAvatar('');
        }
      }
    };
    fetchUserAvatar();
  }, [user]);

  const allMenuItems = [
    { value: '/', label: 'Dashboard', icon: <DashboardIcon /> },
    { value: '/users', label: 'Usuarios', icon: <UsersIcon />, roles: [ROLE_ADMIN] },
    { value: '/teams', label: 'Equipos', icon: <TeamsIcon />, roles: [ROLE_ADMIN] },
    { value: '/jira-projects', label: 'Proyectos JIRA', icon: <CubeIcon />, roles: [ROLE_ADMIN] },
    { value: '/roles', label: 'Roles', icon: <IdCardIcon />, roles: [ROLE_ADMIN] },
    { value: '/holiday-calendars', label: 'Calendarios Festivos', icon: <CalendarIcon />, roles: [ROLE_ADMIN] },
    { value: '/vacations', label: 'Vacaciones', icon: <CalendarIcon /> },
    { value: '/commits', label: 'Commits', icon: <CommitIcon /> },
    { value: '/jira', label: 'JIRA Dashboard', icon: <BarChartIcon />, roles: [ROLE_ADMIN, ROLE_MANAGER] },
    { value: '/config', label: 'Configuración', icon: <GearIcon />, roles: [ROLE_ADMIN] },
  ];

  const menuItems = allMenuItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(role);
  });

  const handleNavigate = (item) => {
    navigate(item.value);
  };

  const headerProps = {
    logoProps: { type: 'dingus-r', url: '/' },
    onMenuToggle: () => setIsMenuOpen(!isMenuOpen),
    isMenuOpen,
    user: {
      name: user?.name || 'Usuario',
      email: user?.email || '',
      avatar: userAvatar,
      items: [{ label: 'Cerrar sesión', type: 'item', onClick: logout }]
    },
    currentLanguage: 'es',
    onLanguageChange: (language) => {},
    onNotificationsClick: () => {},
    onThemeChange: (theme) => {},
    theme: 'light',
    showNotifications: false,
    showLanguageSelector: false,
    showThemeToggle: false,
  };

  const sideMenuProps = {
    items: menuItems,
    collapse: !isMenuOpen,
    value: location.pathname,
    onItemClick: handleNavigate,
    expandOnActive: true,
    defaultExpandAll: false,
  };

  return (
    <Layout header={headerProps} menu={sideMenuProps}>
      <Suspense fallback={<LoadingScreen />}>
        <Outlet />
      </Suspense>
    </Layout>
  );
};

const App = () => {
  return (
    <ThemeProvider {...themeConfig}>
      <AuthProvider>
        <NotificationProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                <Route index element={<DailyDashboardScreen />} />
                <Route path="commits" element={<CommitsViewScreen />} />
                <Route path="users" element={<AdminRoute><UserManagementScreen /></AdminRoute>} />
                <Route path="teams" element={<AdminRoute><TeamsScreen /></AdminRoute>} />
                <Route path="jira-projects" element={<AdminRoute><JiraProjectsScreen /></AdminRoute>} />
                <Route path="roles" element={<AdminRoute><RolesScreen /></AdminRoute>} />
                <Route path="holiday-calendars" element={<AdminRoute><HolidayCalendarManagementScreen /></AdminRoute>} />
                <Route path="vacations" element={<VacationCalendarScreen />} />
                <Route path="jira" element={<ManagerOrAdminRoute><JiraDashboardScreen /></ManagerOrAdminRoute>} />
                <Route path="config" element={<AdminRoute><ConfigurationPanelScreen /></AdminRoute>} />
              </Route>
            </Routes>
            <FeedbackButton />
          </Router>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
